import type { GeomType } from "shared";
import { Button } from "@/components/primitives/Button";
import { FloatingCard } from "@/components/primitives/FloatingCard";
import {
	IconCursor,
	IconLine,
	IconMapPin,
	IconPolygon,
} from "@/components/primitives/icons";
import {
	SegmentedControl,
	type SegmentOption,
} from "@/components/primitives/SegmentedControl";
import { cn } from "@/lib/cn";

export type DrawMode = "select" | GeomType;

export type ViewportTarget = {
	center: [number, number];
	zoom?: number;
	bbox?: [number, number, number, number] | null;
};

type Props = {
	mode: DrawMode;
	onChange: (mode: DrawMode) => void;
	disabled?: boolean;
	draftCount?: number;
	editing?: boolean;
	onFinishDraft?: () => void;
	onCancelDraft?: () => void;
	className?: string;
};

const tools: SegmentOption<DrawMode>[] = [
	{ value: "select", label: "Auswählen", icon: <IconCursor /> },
	{ value: "point", label: "Punkt", icon: <IconMapPin /> },
	{ value: "line", label: "Straße", icon: <IconLine /> },
	{ value: "polygon", label: "Bereich", icon: <IconPolygon /> },
];

function hint(mode: DrawMode, draftCount: number, editing: boolean): string | null {
	if (editing && mode === "polygon") {
		return "Ecke ziehen: verschieben · klicken: entfernen · Kante klicken: Punkt hinzufügen";
	}
	if (editing && mode === "line") {
		return "Punkt ziehen: verschieben · klicken: entfernen · Linie klicken: Punkt hinzufügen";
	}
	if (mode === "point") return "Klicke auf die Karte";
	if (mode === "line") {
		if (draftCount === 0) return "Klicke Startpunkt der Straße";
		if (draftCount === 1) return "Weiterer Punkt · mind. 2 nötig";
		return "Weiter klicken · Fertig oder Doppelklick";
	}
	if (mode === "polygon") {
		if (draftCount === 0) return "Klicke erste Ecke des Bereichs";
		if (draftCount < 3) return `${draftCount}/3 Ecken · weiter klicken`;
		return "Weiter klicken · Fertig oder Doppelklick";
	}
	return null;
}

export function DrawToolbar({
	mode,
	onChange,
	disabled,
	draftCount = 0,
	editing = false,
	onFinishDraft,
	onCancelDraft,
	className,
}: Props) {
	const canFinish =
		(mode === "line" && draftCount >= 2) || (mode === "polygon" && draftCount >= 3);
	const help = hint(mode, draftCount, editing);

	return (
		<div className={cn("pointer-events-auto flex flex-col items-center gap-2", className)}>
			<FloatingCard className="flex flex-wrap items-center justify-center gap-1 p-1">
				<SegmentedControl
					value={mode}
					options={tools}
					onChange={onChange}
					disabled={disabled}
				/>
				{mode === "line" || mode === "polygon" ? (
					<>
						<span className="mx-0.5 h-4 w-px bg-zinc-950/10 dark:bg-white/10" />
						<span className="px-1 text-xs tabular-nums text-fg-muted">
							{draftCount} Pkt
						</span>
						<Button
							size="sm"
							variant="primary"
							disabled={!canFinish}
							onClick={onFinishDraft}
							type="button"
						>
							Fertig
						</Button>
						<Button
							size="sm"
							variant="ghost"
							onClick={onCancelDraft}
							type="button"
							disabled={draftCount === 0}
						>
							Abbruch
						</Button>
					</>
				) : null}
			</FloatingCard>
			{help ? (
				<p className="rounded-[var(--radius-control)] bg-surface px-2.5 py-1 text-xs text-fg-muted shadow-[var(--shadow-float)] ring-1 ring-[var(--ring-hairline)]">
					{help}
				</p>
			) : null}
		</div>
	);
}
